/**
 * The flybrain, live.
 *
 * The OpenDesign app mock (web/design/o1-launchpad-app.html) is the surface,
 * ported 1:1: four working rooms - Brain, Hatch, Collection, Science - under a
 * hash router. The Brain room is the home board (board.ts) around the fly
 * scope (flyscope.ts): a live three.js specimen whose real connectome reacts to
 * the chain's feeds and thoughts. Everything dynamic reads real chain state through the indexer, and the
 * hatch wizard runs the real specimen pipeline in-app (launchpad/specimen-core.mjs),
 * ending at the UNSIGNED prepared transaction - the page never signs.
 */

import "./style.css";
import {
  docsCatalog,
  docsExists,
  docsFlat,
  docsHtml,
  type DocsLang,
  type DocsPage,
} from "./docs.ts";

import {
  fetchHeader,
  fetchHistory,
  fetchPotentials,
  isThought,
  newestThought,
  firingCount,
  fetchTapes,
  newestFirings,
  openStream,
  type Firing,
  type BrainInfo,
  type FeedState,
  type History,
  type Tick,
  type TapeRow as TapeManifestRow,
} from "./api";
import { track } from "./analytics";
import { Board, cellGroups } from "./board";
import { Telemetry } from "./telemetry";
import { cellClassLabel } from "./cell-classes";
import { chipsFor, countLine as countLineMarkup } from "./collection";
import { artSvg, drawAllArt } from "./design-art";
import { decodeTape, summarise, tapeNeurons } from "./tapes";
import { CARD_ADDRESS, connect, disconnect, mintCard, registerClaim, shortAddress } from "./feed";
import { mountFeedPanel, type FeedPanel } from "./feed-panel";
import { mountVitals, type VitalsPanel } from "./vitals";
import {
  foilFor,
  foilTier,
  mountHoloCard,
  type MountHoloOptions,
  type SpecimenFoilInput,
} from "./holo";
import { isGameKey, tr } from "./game/copy";
import { GameView } from "./game/game-view";
import { fetchConnectome, fetchGeometry, fetchMeta } from "./geometry";
import {
  applyStatic,
  currentLang,
  isLang,
  readLang,
  setCurrent,
  setKeyFallback,
  storeLang,
  t,
  type Lang,
  type LangStore,
} from "./i18n";
import {
  artVariant,
  deriveGenome,
  loadAnnotationTables,
  traitsFor,
  fetchProfile,
  paintArt,
  thoughtFromTick,
  type HatchGenome,
  type SpecimenProfile,
  type ThoughtInput,
} from "./hatch";
import { provenThoughts, REGISTRY_ADDRESS, readClaimThoughts, registryClaims } from "./registry";
import { FlyScope } from "./flyscope";

const REDUCED =
  window.matchMedia !== undefined && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** how long the stage's burst seam stays lit after a strike, seconds */
const BURST_SECONDS = 3.2;

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

function setText(id: string, v: string): void {
  const el = document.getElementById(id);

  if (el !== null) el.textContent = v;
}

// ---- the language switch: applied in place, stored when allowed ------------
// The dictionary changes, every data-i18n string re-applies, and the surfaces
// built from live data re-render from what they were built from. Storage is a
// preference, not a requirement: a browser that refuses it still gets a
// working switch for this page view.

/** The real store, or null where the browser refuses storage outright. */
function localStore(): LangStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The data-built surfaces, re-rendered by start() once it has something. */
let rerenderSurfaces: () => void = () => {};

/** The Game plate's own words, rewritten by start() once the plate exists. */
let relabelGame: () => void = () => {};

let relabelBoard: () => void = () => {};

/** The document language and the switcher's own state, under currentLang(). */
function reflectLang(): void {
  const active = currentLang();

  const tag = active === "zh" ? "zh-CN" : active === "vi" ? "vi" : active === "hi" ? "hi" : "en";

  document.documentElement.lang = tag;

  // the hatch section carries the tag too: its own sheet sets CJK leading and
  // tracking by [lang] (web/design/hatch-plate.html, block B7)
  const hatch = document.getElementById("hatchStep3");

  if (hatch !== null) hatch.lang = tag;

  for (const button of document.querySelectorAll<HTMLElement>(".lang-btn")) {
    const on = isLang(button.dataset.lang) && button.dataset.lang === active;

    button.classList.toggle("on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
  }
}

function applyLang(lang: Lang): void {
  setCurrent(lang);

  reflectLang();

  applyStatic();

  renderTape();

  renderWallet();

  relabelGame();

  relabelBoard();

  rerenderSurfaces();
}

const bootLang = readLang(localStore());

// The Game plate keeps its own dictionary; this is how one switch reaches its
// keys without the site dictionary having to carry them
setKeyFallback((lang, key) => (isGameKey(key) ? tr(lang, key) : null));

setCurrent(bootLang);

reflectLang();

applyStatic();

// the home masthead's wall clock: the visitor's own time, beside the chain's counts
const homeClock = document.getElementById("homeClock");

if (homeClock !== null) {
  const tickClock = (): void => {
    homeClock.textContent = new Date().toLocaleTimeString("en-GB");
  };

  tickClock();
  setInterval(tickClock, 1000);
}

for (const button of document.querySelectorAll<HTMLElement>(".lang-btn")) {
  button.addEventListener("click", () => {
    const chosen = button.dataset.lang;

    if (!isLang(chosen) || chosen === currentLang()) return;

    storeLang(localStore(), chosen);
    applyLang(chosen);
  });
}

// ---- the design's display vocabulary, keyed by the real BANC regions -------
// Region names stay Latin in both languages: they are the specimen's genus
// stems (and the filter's stable keys), not UI copy. Side and cell are copy.

const REGION_DISPLAY: ReadonlyMap<string, string> = new Map([
  ["optic_lobe", "Optica"],
  ["central_brain", "Cerebralis"],
  ["ventral_nerve_cord", "Ventralis"],
  ["unknown", "Ignota"],
]);

function sideDisplay(side: string): string {
  const key = side === "left" ? "side.left" : side === "right" ? "side.right" : "side.dorsal";

  return t(key);
}

function cellDisplay(cellClass: string): string {
  if (cellClass === "") return t("cell.fallback");

  return cellClassLabel(currentLang(), cellClass);
}

/** One fired neuron on the tape, with everything its plate needs. */
/** What a display row needs: tonight's window and a registry claim both have it. */
type RowSource = {
  readonly neuron: number;
  readonly tick: number;
  readonly txHash: string;
  readonly genome: HatchGenome;
};

/** A fired neuron from the indexer's window: the wizard's own pick list. */
/**
 * The table's own ceiling, not a product limit: the served window is far smaller
 * than this, so every neuron tonight's thoughts fired is offered, and only a
 * marathon session ever trims the oldest rows.
 */
const SPECIMENS = 320;

type Specimen = RowSource & {
  readonly stateRoot: string;
  readonly raw: Tick;
};

/** A NeuronRegistry claim whose thought the chain confirmed fires its neuron. */
type Claimed = RowSource & {
  readonly token: string;
  readonly block: number;
};

type Row = {
  readonly seed: string;
  readonly id: string;
  readonly binom: string;
  readonly region: string;
  readonly side: string;
  readonly cell: string;
  readonly tick: number;
  readonly tx: string;
  /** the short form of the wallet that fed the thought: the token's owner (D5) */
  readonly owner: string;
  /** the same wallet in full, for the cell's title */
  readonly ownerFull: string;
};

function rowOf(specimen: RowSource): Row {
  const g = specimen.genome;

  return {
    seed: g.symbol,
    id: `N-${g.neuronId}`,
    binom: g.name,
    region: REGION_DISPLAY.get(g.region) ?? "Ignota",
    side: sideDisplay(g.side),
    cell: cellDisplay(g.cell_class),
    tick: specimen.tick,
    tx: shortAddress(specimen.txHash),
    owner: g.feeder === "" ? "-" : shortAddress(g.feeder),
    ownerFull: g.feeder,
  };
}

function chipList(n: { region: string; side: string; cell: string }): string {
  return `<span class="chip">${n.region}</span><span class="chip">${n.side}</span><span class="chip">${n.cell}</span>`;
}

// ---- the wizard renderers (design markup, real data) -----------------------

let picked: Row | null = null;

let pickedNeuron: number | null = null;

let walletAddress: string | null = null;

/** The card the press mints, built from the pick alone: no API, no key. */
type CardPick = {
  readonly genome: HatchGenome;
  readonly neuron: number;
  readonly thoughtTx: string;
  readonly creator: string;
  /** the biology the mint records on chain; see SpecimenCard's own layout note */
  readonly traits: bigint;
};

/**
 * Where the hatch itself stands. `minted` is not the end: the card exists
 * onchain but the registry - the collection's only source of rows - does not
 * know about it yet, and only the claim can say otherwise.
 */
type LaunchState = "idle" | "launching" | "launched" | "claiming" | "claimed" | "unconfirmed";

let lastCard: CardPick | null = null;

/** The picked specimen's own material: what the plate and the press build on. */
type HatchPick = { readonly thought: ThoughtInput; readonly neuron: number };

let pickedHatch: HatchPick | null = null;

let launchState: LaunchState = "idle";

/** The card contract the hatch minted into: constant, so it is not a token. */
let launchedToken: string | null = null;

let launchedTx: string | null = null;

/**
 * The one refusal the hand-off has to remember, as data rather than as a
 * sentence: a language switch re-says it in the other language.
 */
type Note =
  | { readonly kind: "unconfirmed"; readonly addr: string; readonly tx: string }
  | { readonly kind: "claimFailed"; readonly reason: string };

let launchNote: Note | null = null;

/** A remembered refusal in the reader's own language. */
function noteText(note: Note): string {
  if (note.kind === "unconfirmed")
    return t("launch.unconfirmed", { addr: shortAddress(note.addr), tx: note.tx });

  return t("launch.claimFailed", { reason: note.reason });
}

/**
 * A refusal the visitor can answer by pressing again (a wallet that refused):
 * the band says it and leaves the press live. It is kept as the fact itself -
 * which refusal, with what - so it reads in either language.
 */
type Refusal = { readonly kind: "wallet"; readonly reason: string };

let launchRefusal: Refusal | null = null;

/** Whether the next render should move focus to the band (a refusal we caused). */
let focusBand = false;

/**
 * Every mounted foil card, so a view that rebuilds its HTML can destroy them
 * first: the library keeps pointer listeners, and a collection re-renders on
 * every language switch.
 */
const holoCards: Array<() => void> = [];

/** How many cards in a grid may react to the pointer; the rest keep the foil. */
const HOLO_INTERACTIVE = 3;

function clearHoloCards(): void {
  for (const destroy of holoCards) destroy();

  holoCards.length = 0;
}

/** The glow a card casts, in the site's own amber, not the library's default. */
const HOLO_GLOW = "oklch(78% 0.12 76)";

/** The card's own ground: the specimen drawing is light ink and needs a dark field. */
const HOLO_GROUND = "oklch(14.5% 0.024 215)";

/**
 * The specimen drawing with its ground painted in. `artSvg` draws transparent
 * line art for canvases that set their own background; a foil card needs the
 * ground inside the image, or the library's own light base shows through and the
 * specimen washes out.
 */
function groundedArt(seed: string, size: number, variant: number): string {
  const svg = artSvg(seed, size, variant);
  const at = svg.indexOf(">", svg.indexOf("<svg"));

  if (at < 0) return svg;

  return `${svg.slice(0, at + 1)}<rect width="100%" height="100%" fill="${HOLO_GROUND}"/>${svg.slice(at + 1)}`;
}

/** What the drawn face needs beyond the specimen itself. */
type CardFaceParts = {
  readonly tier: string;
  readonly chips: readonly string[];
  readonly facts: ReadonlyArray<readonly [string, string]>;
  readonly footer: readonly [string, string];
};

/**
 * Mounts one specimen's foil card and remembers how to take it down again.
 *
 * The face handed to the library is the drawn card (`cardFaceSvg`), not the bare
 * drawing: the demo's cards carry their frame, their name band and their rules
 * box inside the image, which is what lets the foil play over the whole face.
 */
function mountSpecimenCard(
  host: HTMLElement,
  specimen: { readonly neuron: number; readonly genome: HatchGenome; readonly txHash: string },
  size: number,
  interactive: boolean,
  face?: CardFaceParts,
): void {
  const faceOptions: MountHoloOptions = {
    alt: specimen.genome.name,
    // the demo's cards are its artwork with the library's effects on top; ours are
    // the specimen's own drawing, nothing drawn around it
    art: groundedArt(specimen.genome.seed, size, artVariant(specimen.neuron)),
    glow: HOLO_GLOW,
    interactive,
  };

  // the demo's pill names the card's own rarity, when the caller knows it
  if (face !== undefined) faceOptions.rarity = face.tier;

  const mounted = mountHoloCard(
    host,
    {
      neuronId: specimen.neuron,
      region: specimen.genome.region,
      cellClass: specimen.genome.cell_class,
    },
    faceOptions,
  );

  holoCards.push(mounted.destroy);
}

/** The four foil tiers a card can carry, weakest first: the filter's vocabulary. */
const TIERS = ["common", "uncommon", "rare", "legacy"] as const;

type Tier = (typeof TIERS)[number];

/**
 * A specimen's tier, from the same reading that decides its foil: cell class and
 * the hatch order when the card exists. `degree` (real synapse degree across the
 * tapes) is not known to the page yet, so it never lifts a tier here.
 */
function tierOf(
  specimen: { readonly neuron: number; readonly genome: HatchGenome },
  hatchIndex?: number,
): Tier {
  const input: SpecimenFoilInput = {
    neuronId: specimen.neuron,
    region: specimen.genome.region,
    cellClass: specimen.genome.cell_class,
  };

  if (hatchIndex !== undefined) input.hatchIndex = hatchIndex;

  return foilTier(input);
}

/** The four region and four flow names the trait word indexes into. */
const REGION_WORDS = ["central_brain", "optic_lobe", "ventral_nerve_cord", "unknown"];

const FLOW_WORDS = ["intrinsic", "afferent", "efferent", "unknown"];

/**
 * The card's bio-intelligent attributes, decoded from the very word
 * `SpecimenCard.mint` records on chain (`traitsFor`). What the card face shows
 * is therefore what the token's own `metadataJson` serves: region, side, flow,
 * the annotation table indices, what the thought did, and the art seed prefix.
 */
function specimenFacts(traits: bigint): Array<[string, string]> {
  const region = Number(traits & 3n);
  const flow = Number((traits >> 2n) & 3n);
  const side = Number((traits >> 4n) & 1n);
  const superClass = Number((traits >> 8n) & 0xffn);
  const cellClass = Number((traits >> 16n) & 0xffn);
  const cellType = Number((traits >> 24n) & 0xffffn);
  const synapses = Number((traits >> 40n) & 0x3ffn);
  const spiked = Number((traits >> 50n) & 0x3ffn);
  const fired = Number((traits >> 60n) & 0xffn);
  const seed = ((traits >> 68n) & ((1n << 56n) - 1n)).toString(16).padStart(14, "0");

  return [
    [t("facts.region"), `${REGION_WORDS[region] ?? "unknown"} · #${region}`],
    [t("facts.side"), side === 0 ? "left" : "right"],
    [t("facts.flow"), FLOW_WORDS[flow] ?? "unknown"],
    [t("facts.class"), `${cellClass} · super ${superClass}`],
    [t("facts.type"), `#${cellType}`],
    [t("facts.synapses"), String(synapses)],
    [t("facts.spiked"), String(spiked)],
    [t("facts.fired"), String(fired)],
    [t("facts.seed"), `0x${seed}`],
  ];
}

/** One `dl.facts` block, the component the card design gives its attributes. */
function factsMarkup(facts: ReadonlyArray<[string, string]>): string {
  return (
    `<dl class="facts">` +
    facts
      .map(([key, value]) => `<div class="fact"><dt>${key}</dt><dd>${value}</dd></div>`)
      .join("") +
    `</dl>`
  );
}

/** One of the four tier names, in the reader's language. */
function tierLabel(tier: string): string {
  if (tier === "uncommon") return t("filter.uncommon");

  if (tier === "rare") return t("filter.rare");

  if (tier === "legacy") return t("filter.legacy");

  return t("filter.common");
}

/** The chain explorer's base URL, set from the indexer's own chain info. */
let explorerBase = "https://explorer.arc.io";

/** Re-reads the registry rows; start() points this at its own claim read. */
let refreshClaims: () => void = () => {};

/** The creator the wallet must match: the thought's own feeder (D5). */
function cardCreator(): string | null {
  return lastCard?.creator ?? pickedHatch?.thought.feeder ?? null;
}

let lastProfile: SpecimenProfile | null = null;

/**
 * The picker: one foil card per fired neuron, in the same grid the collection
 * uses. A visitor picks a card here, and the rest of the wizard works on that
 * pick - so the card itself is the control, not a row in a table.
 */
async function renderFiredGrid(specimens: readonly Specimen[]): Promise<void> {
  const grid = document.getElementById("firedGrid");

  if (grid === null) return;

  // the facts are the trait word the mint will record, so the tables have to be
  // in hand before the first card is painted
  await loadAnnotationTables();

  clearHoloCards();
  grid.innerHTML = "";

  for (const [index, specimen] of specimens.entries()) {
    const n = rowOf(specimen);
    const tier = tierOf(specimen);
    const card = document.createElement("li");

    card.className = "card";
    card.dataset.row = String(index);
    card.dataset.odId = `fired-card-${n.seed}`;
    card.dataset.testid = "specimen-card";
    card.dataset.neuron = String(specimen.neuron);
    card.dataset.region = n.region;
    card.dataset.tier = tier;
    card.innerHTML =
      `<div class="foil card-face-host" data-foil data-od-id="fired-foil-${n.seed}"></div>` +
      `<p class="caption">` +
      `<span class="effect" data-testid="specimen-tier">${foilFor({ neuronId: specimen.neuron, region: n.region, cellClass: specimen.genome.cell_class }).effect.toUpperCase()}</span>` +
      `<span class="name" data-testid="specimen-name">${n.binom}</span>` +
      `<span class="ticker" data-testid="specimen-symbol">$${n.id}</span>` +
      `</p>` +
      `<button type="button" class="pick" data-pick="${index}" aria-pressed="false" data-od-id="pick-${n.seed}" data-testid="pick-${n.seed}">${t("th.pick")}</button>`;
    grid.append(card);
  }

  for (const [index, host] of grid.querySelectorAll<HTMLElement>(".foil").entries()) {
    const specimen = specimens[index];

    if (specimen === undefined) continue;

    const n = rowOf(specimen);

    mountSpecimenCard(host, specimen, 300, index < HOLO_INTERACTIVE, {
      tier: tierLabel(tierOf(specimen)),
      chips: [n.region, n.side, n.cell],
      facts: specimenFacts(await traitsFor(specimen.genome)),
      footer: [`N-${n.id.replace(/^N-/, "")}`, "ARC 5042"],
    });
  }

  markPicked(grid);

  const filter = document.querySelector<HTMLElement>("[data-od-id='fired-filter']");

  if (filter !== null) renderFilterBar(filter, [...grid.querySelectorAll<HTMLElement>(".card")]);
}

/** Marks the picked card: the grid is rebuilt on every language switch. */
function markPicked(body: HTMLElement): void {
  const chosen = String(pickedNeuron);

  for (const card of body.querySelectorAll<HTMLElement>("[data-neuron]")) {
    const isPicked = card.getAttribute("data-neuron") === chosen;

    card.classList.toggle("is-picked", isPicked);

    const pick = card.querySelector<HTMLButtonElement>(".pick");

    if (pick !== null) pick.setAttribute("aria-pressed", String(isPicked));
  }

  for (const button of body.querySelectorAll<HTMLElement>(".pick-btn")) {
    const mine = button.closest("tr")?.getAttribute("data-neuron") === chosen;

    button.textContent = mine ? t("pick.done") : t("th.pick");
    button.classList.toggle("picked", mine);
  }
}

function goStep(step: number): void {
  for (const candidate of [1, 2, 3]) {
    const panel = document.querySelector(`[data-step-panel='${candidate}']`);

    if (panel instanceof HTMLElement) panel.hidden = candidate !== step;

    const li = document.querySelector(`[data-step-li='${candidate}']`);

    if (li !== null) {
      li.classList.toggle("current", candidate === step);
      li.classList.toggle("done", candidate < step);
      li.setAttribute("aria-current", candidate === step ? "step" : "false");
    }
  }

  const tracker = document.querySelector(".wiz-tracker");

  if (tracker !== null && step > 1)
    tracker.scrollIntoView({ behavior: REDUCED ? "auto" : "smooth" });
}

function renderPlate(): void {
  const n = picked;

  if (n === null) return;

  const mount = document.getElementById("plateMount");

  if (mount === null) return;

  mount.innerHTML =
    `<div class="plate">` +
    `<div class="plate-art">` +
    `<canvas id="pArt" width="480" height="480" role="img" aria-label="${t("plate.aria", { seed: n.seed })}"></canvas>` +
    `<p class="plate-cap">${t("plate.cap", { seed: n.seed })}</p>` +
    `</div>` +
    `<div class="plate-body">` +
    `<h4 class="binom">${n.binom}</h4>` +
    `<p class="plate-sym">${n.seed}</p>` +
    `<div class="plate-chips">${chipList(n)}</div>` +
    `<div class="plate-sec" data-od-id="plate-profile"><p class="sub-k">${t("plate.profile")}</p><p class="profile-loading">${t("plate.profileLoading")}</p></div>` +
    `<div class="plate-sec"><p class="sub-k">${t("plate.provenance")}</p><p>${t("plate.provenanceText", { id: n.id, tick: String(n.tick), tx: n.tx, seed: n.seed })}</p></div>` +
    `<div class="plate-sec"><p class="sub-k">${t("plate.terms")}</p><p>${t("plate.termsText")}</p></div>` +
    `</div>` +
    `</div>`;
}

function renderProfile(profile: SpecimenProfile): void {
  const section = document.querySelector("[data-od-id='plate-profile']");

  if (section === null) return;

  const temperament = profile.answers.temperament;
  const habitat = profile.answers.habitat;
  const lightAverse = profile.answers.light_averse;

  // SAFETY: the profile contract fixes the answers' shapes per their type tag

  const rows: { readonly k: string; readonly v: number; readonly note: string }[] = [];

  if (temperament !== undefined) {
    const legend = temperament.legend ?? ["docile", "reactive", "volcanic"];
    const top = (legend[Math.round(temperament.score)] ?? "reactive").split(":")[0].trim();

    rows.push({
      k: t("profile.temperament"),
      v: Math.round((temperament.score / 2) * 100),
      note: top,
    });
  }

  if (habitat !== undefined) {
    const entries = Object.entries(habitat.probabilities);

    if (entries.length > 0) {
      const top = entries.reduce((a, b) => (b[1] > a[1] ? b : a));

      rows.push({ k: t("profile.habitat"), v: Math.round(top[1] * 100), note: top[0] });
    }
  }

  if (lightAverse !== undefined) {
    rows.push({ k: t("profile.light"), v: Math.round(lightAverse.noul * 100), note: "noul" });
  }

  const bars = rows
    .map(
      (row) =>
        `<div class="tbar"><span class="tbar-k">${row.k}</span><span class="tbar-track"><span class="tbar-fill" style="width:${row.v}%"></span></span><span class="tbar-v">${row.v}</span></div>`,
    )
    .join("");

  section.innerHTML =
    `<p class="sub-k">${t("plate.profile")}</p>` +
    bars +
    `<p style="margin-top:12px" class="sub-k">${t("profile.source", { src: profile.source })}</p>`;
}

/**
 * The hatch plate: the specimen's own plate, holding the card it becomes.
 */
/** One row of the plate: a real value in the mono register, or the press's promise. */
function plateRow(key: string, value: string | null, pending: string): string {
  return (
    `<div class="plate-sec${value === null ? " is-pending" : ""}">` +
    `<p class="sub-k">${key}</p>` +
    (value === null ? `<p class="pend">${pending}</p>` : `<p class="pv">${value}</p>`) +
    `</div>`
  );
}

/**
 * The hatch plate: the specimen's own plate, holding the card it becomes. Every
 * row comes from the genome and from chain constants, so there is no API to wait
 * for and nothing is promised that the press cannot do.
 */
function renderHatchPlate(genome: HatchGenome): void {
  const mount = document.getElementById("launchMount");
  const who = document.getElementById("whoSigns");

  if (who !== null) who.innerHTML = t("launch.whoV", { addr: shortAddress(genome.feeder) });

  if (mount === null) return;

  const neuron = genome.neuronId;

  clearHoloCards();
  mount.innerHTML =
    `<div class="plate launch-plate" data-feeder="${genome.feeder}">` +
    `<div class="plate-art">` +
    `<div id="launchCard" data-od-id="launch-card"></div>` +
    `<p class="plate-cap">${t("launch.cap", { seed: picked?.seed ?? genome.seed })}</p>` +
    `</div>` +
    `<div class="plate-body">` +
    `<h4 class="binom" id="launchName" data-testid="launch-name">${genome.name}</h4>` +
    `<p class="plate-sym" id="launchSymbol" data-testid="launch-symbol">$${genome.symbol}</p>` +
    `<div class="plate-chips"><span class="chip lit">${t("launch.chipCard")}</span><span class="chip">Arc · 5042</span></div>` +
    (picked === null
      ? ""
      : plateRow(t("prep.specimen"), `<b>${picked.binom}</b> · ${picked.id}`, "")) +
    plateRow(t("prep.neuron"), `<b>#${neuron}</b>`, "") +
    plateRow(
      t("prep.card"),
      t("prep.cardValue", { addr: shortAddress(CARD_ADDRESS) }),
      t("launch.pendCard"),
    ) +
    plateRow(t("prep.cost"), t("prep.costValue"), t("launch.costS")) +
    plateRow(
      t("prep.recipient"),
      t("prep.recipientValue", { addr: shortAddress(genome.feeder) }),
      "",
    ) +
    plateRow(t("sci6.kNetwork"), t("prep.networkValue"), "") +
    plateRow(
      t("prep.claim"),
      t("prep.claimValue", { addr: shortAddress(REGISTRY_ADDRESS) }),
      t("launch.claimS"),
    ) +
    `</div>` +
    `</div>`;

  const card = document.getElementById("launchCard");

  if (card !== null) {
    mountSpecimenCard(card, { neuron, genome, txHash: genome.tx }, 480, true);
  }

  renderLaunch();
}

/** The band tone the design gives each state: quiet, busy, attention, ready. */
type BandTone = "quiet" | "busy" | "attention" | "ready";

/** Everything the band, the press and the note say in one state. */
type Band = {
  readonly tone: BandTone;
  readonly k: string;
  readonly t: string;
  readonly s: string;
  readonly note: string;
  readonly label: string | null;
  readonly busy: boolean;
  readonly done: boolean;
  readonly off: boolean;
};

/**
 * The hand-off's own state, read in one place.
 *
 * The band is the first thing a visitor reads, so every ending of the hatch has
 * to name itself here: what the wallet is being asked for, what the press will do
 * next, and - when it is refused - that nothing was spent and the press stays
 * live.
 */
function bandState(): Band {
  const creator = cardCreator();
  const who = creator === null ? "" : shortAddress(creator);

  const quiet = (k: string, text: string, note: string, off: boolean): Band => ({
    tone: "quiet",
    k,
    t: text,
    s: "",
    note,
    label: null,
    busy: false,
    done: false,
    off,
  });

  if (launchState === "claimed")
    return {
      tone: "ready",
      k: t("launch.bandClaimedK"),
      t: t("launch.bandClaimedT", {
        binom: picked?.binom ?? "",
        id: picked?.id ?? "",
      }),
      s: "",
      note: t("launch.noteClaimed"),
      label: t("launch.claimed"),
      busy: false,
      done: true,
      off: true,
    };

  if (launchState === "launching" || launchState === "claiming")
    return {
      tone: "busy",
      k: t("launch.bandSigningK"),
      t: t("launch.bandSigningT"),
      s: "",
      note:
        launchState === "claiming"
          ? t("launch.claimNote", { addr: shortAddress(launchedToken ?? "") })
          : t("launch.noteSigning"),
      label: launchState === "claiming" ? t("launch.claiming") : t("launch.confirm"),
      busy: true,
      done: false,
      off: true,
    };

  if (launchState === "launched")
    return {
      tone: "ready",
      k: t("launch.bandLaunchedK"),
      t: t("launch.bandLaunchedT"),
      s: "",
      // the claim's own refusal keeps its place here: the press signs it again
      note: launchNote === null ? t("launch.noteLaunched") : noteText(launchNote),
      label: t("launch.claim"),
      busy: false,
      done: false,
      off: false,
    };

  if (launchState === "unconfirmed")
    return {
      tone: "attention",
      k: t("launch.bandUnconfirmedK"),
      t: launchNote === null ? "" : noteText(launchNote),
      s: t("launch.bandUnconfirmedS"),
      note: t("launch.noteOpened"),
      label: null,
      busy: false,
      done: false,
      off: true,
    };

  if (launchRefusal !== null)
    return {
      tone: "attention",
      k: t("launch.bandWalletK"),
      t: t("launch.failed", { reason: launchRefusal.reason }),
      s: t("launch.bandWalletS"),
      // the refusal is the news, and the note is what the visitor can do about it:
      // without a wallet there is nothing to sign, so the press waits for one
      note: walletAddress === null ? t("launch.noWallet") : t("launch.noteRefused"),
      label: null,
      busy: false,
      done: false,
      off: walletAddress === null,
    };

  if (walletAddress === null)
    return quiet(t("launch.bandNoWalletK"), t("launch.bandNoWalletT"), t("launch.noWallet"), true);

  if (creator === null || creator === "")
    return quiet(
      t("launch.bandNoWalletK"),
      t("launch.bandNoWalletT"),
      t("launch.feederUnknown"),
      true,
    );

  if (walletAddress.toLowerCase() !== creator.toLowerCase())
    return {
      tone: "attention",
      k: t("launch.bandWrongWalletK"),
      t: t("launch.bandWrongWalletT"),
      s: "",
      note: t("launch.notCreator", { addr: who }),
      label: null,
      busy: false,
      done: false,
      off: true,
    };

  return quiet(t("launch.bandOpenedK"), t("launch.bandOpenedT"), t("launch.noteOpened"), false);
}

/** The card's own state, written into the band, the press, the note and the links. */
function renderLaunch(): void {
  const go = document.getElementById("launchGo");
  const bandEl = document.getElementById("hatchBand");

  if (!(go instanceof HTMLButtonElement)) return;

  const band = bandState();

  if (bandEl !== null) {
    bandEl.className = `hatch-band is-${band.tone}`;
    // a refusal is what a visitor has to hear first, and it is the only state
    // that interrupts: everything else is a status
    bandEl.setAttribute("role", band.tone === "attention" ? "alert" : "status");
    bandEl.setAttribute("aria-live", band.tone === "attention" ? "assertive" : "polite");
    setText("bandK", band.k);
    setText("bandT", band.t);

    const meta = document.getElementById("bandS");

    if (meta !== null) {
      meta.textContent = band.s;
      meta.hidden = band.s === "";
    }

    const bar = document.getElementById("bandBar");

    if (bar !== null) bar.hidden = !band.busy;
  }

  go.disabled = band.off;
  go.classList.toggle("is-done", band.done);

  if (band.busy) go.setAttribute("aria-busy", "true");
  else go.removeAttribute("aria-busy");

  go.textContent = band.label ?? t("launch.go");

  setText("launchNote", band.note);
  renderLinks();

  // the refusal that a press caused is read out, not just shown (tabindex="-1")
  if (focusBand && band.tone === "attention" && bandEl !== null) {
    focusBand = false;
    bandEl.focus();
  }
}

/** The two links under the note: the card on the explorer, and the mint's own tx. */
function renderLinks(): void {
  const links = document.getElementById("launchLinks");
  const token = document.getElementById("linkToken");
  const tx = document.getElementById("linkTx");

  if (!(links instanceof HTMLElement)) return;

  links.hidden = launchedToken === null;

  if (launchedToken === null) return;

  if (token instanceof HTMLAnchorElement) token.href = explorerAddress(launchedToken);

  const sep = links.querySelector(".links-sep");

  if (tx instanceof HTMLAnchorElement) {
    tx.hidden = launchedTx === null;

    if (launchedTx !== null) tx.href = `${explorerBase}/tx/${launchedTx}`;
  }

  if (sep instanceof HTMLElement) sep.hidden = launchedTx === null;
}

// ---- the Build view: the chain's tapes, and one decoding button ------------

let buildState: { readonly brain: string; readonly tapes: readonly TapeManifestRow[] } | null =
  null;

/** The last decode answer, kept as numbers so the language switch re-says it. */
let buildDecoded: {
  readonly index: number;
  readonly summary: ReturnType<typeof summarise>;
} | null = null;

/** The manifest the indexer walked: one row per tape, payloads fetched on demand. */
function renderBuildTapes(): void {
  const body = document.getElementById("buildTapeBody");

  if (body === null) return;

  if (buildState === null) return;

  setText("buildCount", String(buildState.tapes.length));
  setText("buildBrain", buildState.brain === "" ? "-" : buildState.brain);

  body.innerHTML = buildState.tapes
    .map(
      (tape) =>
        `<tr data-testid="tape-row" data-index="${tape.index}">` +
        `<td class="mono">${tape.index}</td>` +
        `<td class="mono">${shortAddress(tape.address)}</td>` +
        `<td class="mono">${fmt(tape.startNeuron)}</td>` +
        `<td class="mono">${fmt(tape.bytes)}</td>` +
        `<td class="mono" title="${tape.sha256}">${tape.sha256.slice(0, 16)}…</td>` +
        `</tr>`,
    )
    .join("");

  setText(
    "buildTapesState",
    buildState.tapes.length === 0
      ? t("bld.manifest.empty")
      : t("bld.manifest.count", {
          n: String(buildState.tapes.length),
          brain: shortAddress(buildState.brain),
        }),
  );
}

/** The decode line: numbers in, sentence out, so the switch can re-say it. */
function renderBuildDecoded(): void {
  if (buildDecoded === null) return;

  const { index, summary } = buildDecoded;

  setText(
    "buildDecodeOut",
    t("bld.decode.out", {
      index: String(index),
      neurons: fmt(summary.neurons),
      synapses: fmt(summary.synapses),
      targets: fmt(summary.targets),
      lightest: fmt(summary.lightest),
      heaviest: fmt(summary.heaviest),
    }),
  );
}

/** The live values a faithful run needs, from the brain's own report. */
function renderBuildNeeds(brain: BrainInfo | null): void {
  const values: readonly [string, number | undefined][] = [
    ["threshold", brain?.threshold],
    ["segments", brain?.segments],
    ["sensory", brain?.sensory],
    ["words", brain?.words],
  ];

  for (const [key, value] of values) {
    const node = document.querySelector(`[data-need="${key}"]`);

    if (node !== null && value !== undefined) node.textContent = fmt(value);
  }
}

/**
 * The Build view's one interactive piece: fetch a payload the indexer already
 * has, decode it here with the same rules tools/tapes.py writes with, and print
 * the shape. Nothing is faked: if the layout were wrong, this says so.
 */
function mountBuild(): void {
  const form = document.getElementById("buildDecode");
  const input = document.getElementById("buildTapeIndex");

  void fetchTapes()
    .then((manifest) => {
      buildState = manifest;
    })
    .catch(() => {
      buildState = { brain: "", tapes: [] };
    })
    .finally(() => {
      renderBuildTapes();
      renderBuildIndex();
    });

  if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) return;

  form.addEventListener("submit", (event) => {
    event.preventDefault();

    const index = Number.parseInt(input.value, 10);
    const tape = buildState?.tapes.find((candidate) => candidate.index === index);

    if (tape === undefined) {
      setText("buildDecodeOut", t("bld.decode.error", { index: String(index), reason: "?" }));

      return;
    }

    setText("buildDecodeOut", t("bld.manifest.reading"));

    void fetch(`/api/tape/${tape.index}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        return new Uint8Array(await response.arrayBuffer());
      })
      .then((bytes) => {
        buildDecoded = {
          index: tape.index,
          summary: summarise(decodeTape(bytes, tape.startNeuron), tapeNeurons(bytes)),
        };
        renderBuildDecoded();
      })
      .catch((error) => {
        setText(
          "buildDecodeOut",
          t("bld.decode.error", {
            index: String(tape.index),
            reason: error instanceof Error ? error.message : "?",
          }),
        );
      });
  });
}

/**
 * The card the pick becomes, built from the pick alone.
 *
 * There is no API here any more: o1's prepare, its key, its origin lock and its
 * refusals are gone, so the only thing that can be slow is the genome
 * derivation (annotations loaded once, cached). A visitor who moves on while it
 * runs gets no plate from a hatch that is not theirs.
 */
async function prepareCard(hatch: HatchPick): Promise<boolean> {
  launchNote = null;
  launchRefusal = null;

  let genome: HatchGenome;

  try {
    genome = await deriveGenome(hatch.thought, hatch.neuron);
  } catch (error) {
    if (pickedHatch !== hatch) return false;

    launchRefusal = {
      kind: "wallet",
      reason: error instanceof Error ? error.message : t("feed.unknownError"),
    };
    renderLaunch();

    return false;
  }

  if (pickedHatch !== hatch) return false;

  lastCard = {
    genome,
    neuron: hatch.neuron,
    thoughtTx: hatch.thought.tx,
    creator: hatch.thought.feeder,
    traits: await traitsFor(genome),
  };
  renderHatchPlate(genome);
  track("hatch_card_ready", { symbol: genome.symbol });

  return true;
}

/**
 * The hatch itself: one press, two signatures - the card mint, then the claim.
 *
 * The mint is the only transaction that makes a card exist: `SpecimenCard.mint`
 * refuses a second mint of the same neuron, so the contract itself carries the
 * one-per-neuron promise. The registry claim that follows is what the
 * collection reads, and a refused second signature leaves a minted card whose
 * claim can be signed again.
 */
async function mintHatch(): Promise<void> {
  if (launchState === "launched") {
    await claimHatch();

    return;
  }

  const hatch = pickedHatch;

  if (hatch === null) return;

  if (lastCard === null) {
    const ready = await prepareCard(hatch);

    if (!ready) {
      focusBand = true;
      renderLaunch();

      return;
    }
  }

  const card = lastCard;

  if (card === null || walletAddress === null) return;

  // the card belongs to the thought's feeder (D5) and to nobody else
  if (walletAddress.toLowerCase() !== card.creator.toLowerCase()) return;

  launchNote = null;
  launchState = "launching";
  renderLaunch();
  track("hatch_card_signed", { neuron: String(card.neuron) });

  let hash: string;

  try {
    hash = await mintCard(card.creator, card.neuron, card.thoughtTx, card.traits);
  } catch (error) {
    launchState = "idle";
    // textContent, not markup: a wallet's refusal reason is shown as it came, and
    // the press stays live - a refused signature is not a dead end
    launchRefusal = {
      kind: "wallet",
      reason: error instanceof Error ? error.message : t("feed.unknownError"),
    };
    focusBand = true;
    renderLaunch();

    return;
  }

  launchedTx = hash;
  launchedToken = CARD_ADDRESS;
  launchState = "launched";
  renderLaunch();

  await claimHatch();
}

/** The second signature: the registry row the collection reads. */
async function claimHatch(): Promise<void> {
  const card = lastCard;

  if (card === null || launchedToken === null || walletAddress === null) return;

  const creator = card.creator;

  launchNote = null;
  launchState = "claiming";
  renderLaunch();

  try {
    await registerClaim(creator, REGISTRY_ADDRESS, card.neuron, card.thoughtTx, launchedToken);
  } catch (error) {
    launchState = "launched";
    launchNote = {
      kind: "claimFailed",
      reason: error instanceof Error ? error.message : t("feed.unknownError"),
    };
    renderLaunch();

    return;
  }

  launchState = "claimed";
  renderLaunch();
  track("hatch_claimed", { token: launchedToken });

  // the row exists now: the collection must not keep showing its old list
  refreshClaims();
}

/** The picked specimen's plate: copy, art, and the biographer's profile. */
function renderPickedPlate(specimen: Specimen, feederByTx: ReadonlyMap<string, string>): void {
  renderPlate();

  // the real art plate, painted from the launchpad's art core
  const canvas = document.getElementById("pArt");

  if (canvas instanceof HTMLCanvasElement) {
    void paintArt(canvas, specimen.genome.seed, artVariant(specimen.neuron)).catch(() => undefined);
  }

  if (lastProfile !== null) {
    renderProfile(lastProfile);

    return;
  }

  // the biographer's profile, from the real launchpad core
  const feeder = feederByTx.get(specimen.txHash);

  if (feeder === undefined) return;

  void fetchProfile(specimen.genome, import.meta.env.VITE_JEV_API_KEY ?? "")
    .then((profile) => {
      if (pickedNeuron !== specimen.neuron) return;

      lastProfile = profile;
      renderProfile(profile);
    })
    .catch(() => {
      // the template fallback already covers the plate; nothing to do here
    });
}

function wireWizard(specimens: readonly Specimen[], feederByTx: ReadonlyMap<string, string>): void {
  const grid = document.getElementById("firedGrid");

  grid?.addEventListener("click", (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const pick = target?.closest<HTMLElement>("[data-pick]") ?? null;

    if (pick === null) return;

    const specimen = specimens[Number(pick.dataset.pick)];

    if (specimen === undefined) return;

    pickedNeuron = specimen.neuron;
    lastCard = null;
    lastProfile = null;
    picked = rowOf(specimen);
    resetLaunch();

    // the press prepares from here: the specimen's own thought, and nobody's API yet
    const feeder = feederByTx.get(specimen.txHash) ?? "";

    pickedHatch =
      feeder === ""
        ? null
        : { thought: thoughtFromTick(specimen.raw, feeder), neuron: specimen.neuron };

    const grid = pick.closest<HTMLElement>("#firedGrid");

    if (grid !== null) markPicked(grid);

    if (toStep2 instanceof HTMLButtonElement) toStep2.disabled = false;

    renderPickedPlate(specimen, feederByTx);
    goStep(2);
    track("specimen_selected", { symbol: picked.seed });
  });

  const toStep2 = document.getElementById("toStep2");

  toStep2?.addEventListener("click", () => {
    goStep(2);
  });

  document.getElementById("backTo1")?.addEventListener("click", () => {
    goStep(1);
  });

  document.getElementById("toStep3")?.addEventListener("click", () => {
    if (pickedNeuron === null) return;

    goStep(3);

    // the step opens on the specimen's own numbers: the card is derived here, so
    // the plate is complete before anyone presses anything
    lastCard = null;
    resetLaunch();

    const specimen = specimens.find((candidate) => candidate.neuron === pickedNeuron);

    if (specimen === undefined) return;

    const hatch = pickedHatch;

    if (hatch !== null) void prepareCard(hatch);
  });

  document.getElementById("backTo2")?.addEventListener("click", () => {
    goStep(2);
  });

  document.getElementById("launchGo")?.addEventListener("click", () => {
    void mintHatch();
  });
}

/** A new specimen or a new card: nothing from the previous hatch survives. */
function resetLaunch(): void {
  launchState = "idle";
  launchedToken = null;
  launchedTx = null;
  launchNote = null;
  launchRefusal = null;
  focusBand = false;
}

/**
 * The onchain artifact of a hatched specimen, on the explorer. Rows claimed
 * through o1 point at their real token; rows claimed since the card exists point
 * at the card contract. Both are the same cell, because both are the same fact:
 * this specimen has an address on Arc.
 */
function explorerAddress(address: string): string {
  return `${explorerBase}/address/${address}`;
}

/**
 * The claim grid, the region chips and the count line, painted from one
 * registry read. The read is the expensive half; the paint re-runs in place
 * when the language changes.
 */
async function renderCollectionGrid(claimed: readonly Claimed[]): Promise<void> {
  const gallery = document.getElementById("gallery");

  await loadAnnotationTables();
  const filter = document.querySelector("[data-od-id='collection-filter']");
  const noClaims = document.querySelector("[data-od-id='collection-note']");

  if (noClaims instanceof HTMLElement) noClaims.hidden = claimed.length > 0;

  if (gallery !== null) {
    clearHoloCards();
    gallery.innerHTML = "";

    for (const specimen of claimed) {
      const card = document.createElement("li");
      const n = rowOf(specimen);
      const tier = tierOf(specimen, claimed.indexOf(specimen));

      card.className = "card";
      card.dataset.testid = "collection-claim";
      card.dataset.region = n.region;
      card.dataset.tier = tier;
      card.dataset.neuron = String(specimen.neuron);
      card.innerHTML =
        `<div class="foil" data-foil data-od-id="claim-foil-${n.seed}"></div>` +
        `<div class="cap">` +
        `<p class="binom" data-testid="claim-name">${specimen.genome.name}</p>` +
        `<p class="cap-line"><span class="ticker" data-testid="claim-symbol">${specimen.genome.symbol}</span>` +
        `<span class="tier-word" data-testid="claim-tier">${tierLabel(tier)}</span></p>` +
        `<ul class="chips">` +
        `<li class="chip" title="${n.region}">${n.region}</li>` +
        `<li class="chip">${n.side}</li>` +
        `<li class="chip chip-verbatim">${n.cell}</li>` +
        `</ul>` +
        factsMarkup(specimenFacts(await traitsFor(specimen.genome))) +
        `<div class="card-foot">` +
        `<div class="rec">` +
        `<div class="rec-row"><dt>${t("prep.card")}</dt><dd>${shortAddress(specimen.token)}</dd></div>` +
        `<a class="xlink" data-testid="claim-buy" href="${explorerAddress(specimen.token)}" target="_blank" rel="noopener noreferrer">` +
        `<span>${t("col.buy")}</span>` +
        `<svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M3 9L9 3M9 3H4.5M9 3v4.5"/></svg>` +
        `</a>` +
        `<span class="minted"><i aria-hidden="true"></i>${t("card.claim")}</span>` +
        `</div>` +
        `</div>`;
      gallery.append(card);
    }

    // the foil face, mounted per card: the first few react to the pointer, the
    // rest keep the foil as a still layer
    for (const [index, host] of gallery.querySelectorAll<HTMLElement>(".foil").entries()) {
      const specimen = claimed[index];

      if (specimen === undefined) continue;

      mountSpecimenCard(host, specimen, 300, index < HOLO_INTERACTIVE);
    }
  }

  // the filter bar both grids share, in the design's own markup
  if (filter instanceof HTMLElement && gallery !== null) {
    filter.hidden = claimed.length === 0;
    renderFilterBar(filter, [...gallery.querySelectorAll<HTMLElement>(".card")]);
  }

  // the empty state the design gives a registry with no rows yet
  const empty = document.querySelector<HTMLElement>("[data-od-id='empty-state-note']");

  if (empty !== null) empty.hidden = claimed.length > 0;
}

/**
 * Runs a chain read and paints it, retrying the read itself while Arc refuses
 * to answer. Nothing is painted for a read that never answered: the count line
 * keeps saying it is reading, and the empty state stays reserved for a chain
 * that really answered with no claims.
 */
async function retried<T>(
  read: () => Promise<T>,
  paint: (value: T) => void,
  rounds = 3,
  spacingMs = 6_000,
): Promise<void> {
  for (let round = 1; round <= rounds; round += 1) {
    try {
      paint(await read());

      return;
    } catch {
      if (round < rounds) await new Promise((done) => window.setTimeout(done, spacingMs));
    }
  }
}

/**
 * The hatched register on the Hatch view: one row per registry claim, in the
 * registry's own hatch order. The row is the specimen's market - it opens the
 * o1 page, and the market cell carries the same link for keyboards.
 */
function renderHatchedList(claimed: readonly Claimed[]): void {
  const body = document.getElementById("hatchedBody");
  const scroll = document.getElementById("hatchedScroll");
  const empty = document.getElementById("hatchedEmpty");
  const count = document.getElementById("hatchedCount");

  if (empty instanceof HTMLElement) empty.hidden = claimed.length > 0;

  if (scroll instanceof HTMLElement) scroll.hidden = claimed.length === 0;

  if (count !== null) count.innerHTML = t("hlist.count", { n: String(claimed.length) });

  if (body === null) return;

  let html = "";

  for (const specimen of claimed) {
    const n = rowOf(specimen);
    const market = explorerAddress(specimen.token);

    html +=
      `<tr data-od-id="hatched-row-${specimen.neuron}" data-testid="hatched-row" data-token="${specimen.token}" data-href="${market}">` +
      `<td><div class="thumb"><svg data-seed="${n.seed}" data-size="120" data-variant="${artVariant(specimen.neuron)}" aria-hidden="true"></svg></div></td>` +
      `<td><span class="n-id">${n.id}</span></td>` +
      `<td><span class="binom" data-testid="hatched-name">${n.binom}</span></td>` +
      `<td><div class="chips">${chipList(n)}</div></td>` +
      `<td>T.${n.tick}</td>` +
      `<td class="txc" data-testid="hatched-token"><span class="tok-sym">${n.seed}</span> ${shortAddress(specimen.token)}</td>` +
      `<td><a class="market-link" data-testid="hatched-market" href="${market}" target="_blank" rel="noopener noreferrer" aria-label="${t("hlist.openAria", { name: n.binom })}">${t("hlist.open")}<span aria-hidden="true"> ↗</span></a></td>` +
      `</tr>`;
  }

  body.innerHTML = html;
  drawAllArt(body);
}

// a click anywhere on a row opens its market; a click on the row's own link is
// the browser's to handle, so it never opens twice
document.getElementById("hatchedBody")?.addEventListener("click", (event) => {
  const target = event.target;

  if (!(target instanceof HTMLElement) || target.closest("a") !== null) return;

  const href = target.closest("tr[data-href]")?.getAttribute("data-href");

  if (href === null || href === undefined) return;

  window.open(href, "_blank", "noopener,noreferrer");
});

/**
 * Every claim the registry has written, each one joined to the thought its own
 * transaction recorded and confirmed against that receipt's fired set.
 *
 * This is the whole claim index: it does not depend on the local indexer's
 * window, which lags the chain and therefore knows nothing of a hatch made
 * before it scanned that far. A claim the chain will not confirm is left out;
 * a read the node refused is retried before anything is rendered.
 */
async function readClaimed(brain: string): Promise<Claimed[]> {
  const claims = [...(await registryClaims()).values()];

  // the indexer's own rows carry the thought its walk proved, so only the claims
  // it could not prove cost a receipt read on the public node
  const { proven, unproven } = provenThoughts(claims);
  const thoughts = new Map([...proven, ...(await readClaimThoughts(unproven, brain))]);
  const claimed: Claimed[] = [];

  for (const claim of claims) {
    const thought = thoughts.get(claim.neuron);

    if (thought === undefined) continue;

    claimed.push({
      neuron: claim.neuron,
      tick: thought.tick,
      txHash: claim.thoughtTxHash,
      block: claim.block,
      token: claim.token,
      genome: await deriveGenome(thought, claim.neuron),
    });
  }

  // the registry's own order: the newest hatch first
  return claimed.sort((left, right) => right.block - left.block);
}

/**
 * The filter bar both grids share, in the design's own markup
 * (web/design/specimen-cards.html): a tier group, then a region group, each a
 * fieldset of radio-backed chips carrying their own count, and the result line
 * with its clear control. Tier is the first cut because it is the biology's own
 * reading (`foilFor`), region the second.
 */
function renderFilterBar(host: HTMLElement, cards: readonly HTMLElement[]): void {
  const counts = (read: (card: HTMLElement) => string) => {
    const map = new Map<string, number>();

    for (const card of cards) map.set(read(card), (map.get(read(card)) ?? 0) + 1);

    return map;
  };

  const tierCounts = counts((card) => card.dataset.tier ?? "common");
  const regionCounts = counts((card) => card.dataset.region ?? "");
  const tierRow = host.querySelector<HTMLElement>("[data-row='tier']");
  const regionRow = host.querySelector<HTMLElement>("[data-row='region']");
  const scope = host.dataset.filter ?? "grid";
  const keep = readFilters(host);

  const chip = (group: "tier" | "region", value: string, label: string, count: number): string => {
    const input = `${scope}-${group}-${value}`;
    const on = (group === "tier" ? keep.tier : keep.region) === value;

    return (
      `<span class="fwrap">` +
      `<input type="radio" name="${scope}-${group}" id="${input}" value="${value}"${on ? " checked" : ""}>` +
      `<label class="fchip" for="${input}" data-zero="${count === 0 ? 1 : 0}" title="${label}">` +
      `<span>${label}</span><span class="fchip-n">${count}</span>` +
      `</label></span>`
    );
  };

  if (tierRow !== null)
    tierRow.innerHTML =
      chip("tier", "all", t("filter.all"), cards.length) +
      TIERS.map((tier) => chip("tier", tier, tierLabel(tier), tierCounts.get(tier) ?? 0)).join("");

  if (regionRow !== null)
    regionRow.innerHTML =
      chip("region", "all", t("filter.allRegions"), cards.length) +
      chipsFor(cards.map((card) => card.dataset.region ?? ""))
        .filter((region) => region !== "all")
        .map((region) => chip("region", region, region, regionCounts.get(region) ?? 0))
        .join("");

  applyFilters(host);
}

/** Which chip is on in one group: the radio the design renders is the state. */
type ChipState = { tier: string; region: string };

function readFilters(host: HTMLElement): ChipState {
  const read = (group: "tier" | "region") =>
    host.querySelector<HTMLInputElement>(`input[name$='-${group}']:checked`)?.value ?? "all";

  return { tier: read("tier"), region: read("region") };
}

/**
 * Hides the cards the two filters exclude, keeps the counts honest, shows the
 * design's empty state when nothing matches, and lets the clear control say when
 * there is nothing to clear.
 */
function applyFilters(host: HTMLElement): void {
  const { tier, region } = readFilters(host);
  const grid = host.parentElement?.querySelector<HTMLElement>(".grid");
  const result = host.querySelector<HTMLElement>(".result");
  const clear = host.querySelector<HTMLButtonElement>(".clear");
  const empty = host.parentElement?.querySelector<HTMLElement>(".empty");
  const why = empty?.querySelector<HTMLElement>(".empty-b") ?? null;
  const isCollection = (host.dataset.filter ?? "") === "collection";

  let shown = 0;
  let total = 0;

  if (grid !== null && grid !== undefined)
    for (const card of grid.querySelectorAll<HTMLElement>(".card")) {
      total += 1;

      const visible =
        (tier === "all" || card.dataset.tier === tier) &&
        (region === "all" || card.dataset.region === region);

      card.hidden = !visible;

      if (visible) shown += 1;
    }

  if (clear !== null && clear !== undefined) clear.disabled = tier === "all" && region === "all";

  if (empty !== null && empty !== undefined) {
    empty.hidden = shown > 0 || total === 0;

    if (why !== null)
      why.textContent =
        tier !== "all"
          ? t("filter.emptyTier", { tier: tierLabel(tier) })
          : t("filter.emptyRegion", { region });
  }

  if (result === null || result === undefined) return;

  // the collection keeps the design's count line; the picker reports its own share
  if (isCollection) result.innerHTML = countLineMarkup(currentLang(), region, shown, total);
  // innerHTML: this line carries its number in markup, like the design's own
  else result.innerHTML = t("filter.result", { shown: String(shown), total: String(total) });
}

for (const host of document.querySelectorAll<HTMLElement>("[data-filter]")) {
  host.addEventListener("change", (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === "radio")
      applyFilters(host);
  });

  for (const clear of host.querySelectorAll<HTMLButtonElement>(".clear"))
    clear.addEventListener("click", () => {
      for (const radio of host.querySelectorAll<HTMLInputElement>(
        "input[type='radio'][value='all']",
      ))
        radio.checked = true;

      applyFilters(host);
    });
}

/**
 * The Build page's contents index. The page is a catalogue several screens tall,
 * so it says what is on it: one entry per chapter, taken from the headings in the
 * document - never a hand-kept list that would drift as soon as a chapter moves.
 */
function renderBuildIndex(): void {
  const view = document.getElementById("view-build");
  const head = view?.querySelector<HTMLElement>(".view-head");

  if (view === null || head === null || head === undefined) return;

  const entries: Array<[string, string]> = [];

  for (const heading of view.querySelectorAll<HTMLElement>("h3")) {
    // the chapters carry their plate name in data-od-id, not in an id: the index
    // gives each one the anchor it needs, so a link can point at a chapter
    const chapter = heading.closest<HTMLElement>("[data-od-id]");

    if (chapter === null) continue;

    const slug = chapter.dataset.odId ?? "";

    if (slug === "" || slug === "view-build") continue;

    if (chapter.id === "" || chapter.id === "view-build") chapter.id = slug;

    entries.push([chapter.id, heading.textContent?.trim() ?? ""]);
  }

  if (entries.length === 0) return;

  const existing = view.querySelector<HTMLElement>(".build-toc");

  if (existing !== null) existing.remove();

  const toc = document.createElement("nav");

  toc.className = "build-toc";
  toc.setAttribute("aria-label", t("bld.index"));
  toc.innerHTML =
    `<p>${t("bld.index")}</p><ol>` +
    entries.map(([id, title]) => `<li><a href="#${id}">${title}</a></li>`).join("") +
    `</ol>`;

  head.after(toc);
}

/**
 * The GitBook surface. Three things, each from a source a contributor owns:
 * the sidebar from `docs/SUMMARY.md`, the page from its Markdown file, and the
 * contents list from the headings on that page. Nothing is a second copy.
 */
function mountDocs(slug: string): void {
  const host = document.getElementById("docsBody");

  if (host === null) return;

  const lang: DocsLang = currentLang() === "zh" ? "zh" : "en";
  const file = docsExists(slug) ? slug : "README.md";
  const flat = docsFlat(lang);
  const at = flat.findIndex((page) => page.file === file);

  const sidebar = docsCatalog(lang)
    .map(
      (section) =>
        `<p class="gb-sec">${section.title}</p><ul>` +
        section.pages
          .map(
            (page) =>
              `<li><a href="#docs/${page.file}"` +
              (page.file === file ? ` class="is-current" aria-current="page"` : ``) +
              `>${page.title}</a></li>`,
          )
          .join("") +
        `</ul>`,
    )
    .join("");

  const link = (page: DocsPage | undefined, dir: string): string => {
    if (page === undefined) return `<span class="gb-step is-off"></span>`;

    return `<a class="gb-step" href="#docs/${page.file}"><span>${t(dir === "prev" ? "docs.prev" : "docs.next")}</span><b>${page.title}</b></a>`;
  };

  host.innerHTML =
    `<nav class="gb-side" aria-label="${t("docs.index")}">${sidebar}</nav>` +
    `<article class="gb-page">${docsHtml(file, lang)}` +
    `<footer class="gb-foot">` +
    `<div class="gb-steps">${link(flat[at - 1], "prev")}${link(flat[at + 1], "next")}</div>` +
    `</footer></article>` +
    `<nav class="gb-toc"></nav>`;

  // heading anchors and the contents list, from the headings of this page
  const heads = [...host.querySelectorAll<HTMLElement>(".gb-page h2, .gb-page h3")];
  const toc = host.querySelector<HTMLElement>(".gb-toc");
  const used = new Set<string>();
  const items: string[] = [];

  for (const head of heads) {
    const base =
      (head.textContent ?? "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, "-")
        .replace(/^-|-$/g, "") || "section";

    let id = base;
    let n = 2;

    while (used.has(id)) id = `${base}-${n++}`;

    used.add(id);
    head.id = id;
    items.push(
      `<li><a href="#docs/${file}" data-anchor="${id}" class="${head.tagName === "H3" ? "is-sub" : ""}">${head.textContent}</a></li>`,
    );
  }

  if (toc !== null) {
    toc.innerHTML =
      items.length === 0 ? "" : `<p>${t("docs.onThisPage")}</p><ul>${items.join("")}</ul>`;

    for (const a of toc.querySelectorAll("a")) {
      a.addEventListener("click", (event) => {
        event.preventDefault();

        const target = document.getElementById(a.dataset.anchor ?? "");

        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }
}

/** One tape row: its markup is re-rendered from this as languages switch. */
type TapeRow = () => string;

const tapeRows: TapeRow[] = [];

function trimTape(tape: HTMLElement): void {
  while (tape.children.length > 4) {
    const last = tape.lastChild;

    if (last === null) break;

    tape.removeChild(last);
  }
}

function tapeItem(html: string): HTMLElement {
  const item = document.createElement("span");

  item.className = "tk";
  item.dataset.testid = "thought-row";
  item.dataset.tapeRow = "";
  item.innerHTML = html;

  return item;
}

function pushTicker(row: TapeRow): void {
  const tape = document.getElementById("tickerItems");

  if (tape === null) return;

  tapeRows.unshift(row);

  if (tapeRows.length > 4) tapeRows.length = 4;

  tape.prepend(tapeItem(row()));
  trimTape(tape);
}

/** Rebuilds the pushed rows; the design's demo lines stay as markup. */
function renderTape(): void {
  const tape = document.getElementById("tickerItems");

  if (tape === null) return;

  for (const item of tape.querySelectorAll("[data-tape-row]")) item.remove();

  for (const row of [...tapeRows].reverse()) tape.prepend(tapeItem(row()));

  trimTape(tape);
}

/**
 * One line of the thought tape: a thought, named by the tick the brain gave it.
 * The indexer's per-block activity rows never enter the tape - the owner asked
 * for a tape of thoughts only, so a quiet chain reads as a quiet tape.
 */
function tapeLabel(tick: Tick, thoughts: number, burned: string | null): TapeRow {
  const n = tick.fired.length;

  return () =>
    burned === null
      ? t("tape.fired", { tick: fmt(tick.tick), fired: fmt(n) })
      : t("tape.tick", {
          tick: fmt(tick.tick),
          thoughts: fmt(thoughts),
          fired: fmt(n),
          burned,
        });
}

/** The wallet's own state; its button's copy answers to it in every language. */
type WalletState = "idle" | "connected" | "missing";

let walletState: WalletState = "idle";

/** The wallet button: renderWallet is its only writer of copy and title. */
function renderWallet(): void {
  const button = document.getElementById("walletBtn");

  if (!(button instanceof HTMLButtonElement)) return;

  const connected = walletState === "connected";

  button.classList.toggle("connected", connected);
  button.setAttribute("aria-pressed", connected ? "true" : "false");

  if (connected) {
    button.innerHTML = `<span class="chip lit" data-testid="chain-badge">ARC 5042</span><span class="wallet-addr" data-testid="wallet-address">${shortAddress(walletAddress ?? "")}</span>`;
    button.title = t("wallet.connected");

    return;
  }

  button.textContent = t("wallet.connect");
  button.title = t(walletState === "missing" ? "wallet.missing" : "wallet.title");
}

async function start(): Promise<void> {
  let stageView: FlyScope | null = null;
  let gameView: GameView | null = null;

  // ---- the design's hash router, verbatim ----------------------------------
  const ROUTES = ["brain", "game", "hatch", "build", "docs", "collection"];

  // The bare URL is the front door, and the front door is the brain. A visitor's
  // last view used to be remembered and replayed here, which meant the site
  // opened on whatever they looked at last instead of its own
  // main page - the hash is the whole router now.
  const currentRoute = (): string => {
    const h = (location.hash || "").replace("#", "").split("/")[0];

    return ROUTES.includes(h) ? h : "brain";
  };

  /** The page a docs deep link names: #docs/what-a-run-needs */
  const docsSlug = (): string => {
    const rest = (location.hash || "").replace("#", "").replace(/^docs\/?/, "");

    if (rest === "") return "README.md";

    // a deep link may name the page with or without its extension
    return rest.endsWith(".md") ? rest : `${rest}.md`;
  };

  const showRoute = (route: string): void => {
    for (const r of ROUTES) {
      const view = document.getElementById(`view-${r}`);

      if (view instanceof HTMLElement) {
        view.hidden = r !== route;
        view.setAttribute("aria-hidden", r !== route ? "true" : "false");
      }
    }

    stageView?.setPaused(route !== "brain");
    gameView?.setActive(route === "game");

    for (const link of document.querySelectorAll(".nav-links a")) {
      if (link.getAttribute("data-route") === route) {
        link.setAttribute("aria-current", "page");
      } else {
        link.removeAttribute("aria-current");
      }
    }

    if (route === "docs") mountDocs(docsSlug());

    window.scrollTo(0, 0);
  };

  window.addEventListener("hashchange", () => {
    showRoute(currentRoute());
  });
  showRoute(currentRoute());

  // ---- wallet: real connect / disconnect ------------------------------------
  renderWallet();

  // the feed panel mounts before this point and is told about every change
  let feedPanel: FeedPanel | null = null;
  let vitals: VitalsPanel | null = null;
  /** The visitor's own burn: the thought it opens arrives with the same hash. */
  let mineTx = "";

  /** Connect or drop the wallet. The nav button and the feed panel share it. */
  const toggleWallet = async (): Promise<void> => {
    if (walletState === "connected") {
      await disconnect();

      walletState = "idle";
      walletAddress = null;
    } else {
      try {
        const wallet = await connect();

        walletAddress = wallet.address;
        walletState = "connected";
        track("wallet_connected", { chain: wallet.chainId });
      } catch {
        walletState = "missing";
      }
    }

    renderWallet();

    // a wallet arriving is what unlocks the hatch's launch button
    renderLaunch();

    feedPanel?.walletChanged();
  };

  document.getElementById("walletBtn")?.addEventListener("click", () => {
    void toggleWallet();
  });

  // ---- real history (one retry: the public Arc RPC rate-limits in bursts) ---
  let history: History;

  try {
    history = await fetchHistory();
  } catch {
    await new Promise((resolve) => window.setTimeout(resolve, 2000));

    try {
      history = await fetchHistory();
    } catch {
      const showUnreachable = (): void => {
        setText("firedCount", t("hatch.firedCount", { n: "0" }));
        setText("stageTick", t("stage.live", { n: "…" }));
      };

      showUnreachable();
      rerenderSurfaces = showUnreachable;
      pushTicker(() => t("tape.unreachable"));

      return;
    }
  }

  // the brain's own report, kept so the feed panel can start from it: the
  // header read finishes before the stage is mounted, so the panel cannot be the
  // only place that remembers it
  let feedState: FeedState | null = null;

  const readHeader = async (): Promise<void> => {
    const header = await fetchHeader();

    feedState = header.state;

    // the brain's own report: the feed panel burns what this says it charges,
    // and the state rail reads the wiring, the seal and the tick from it
    feedPanel?.setChain(header.state);

    if (header.brain !== null) vitals?.updateBrain(header.brain);
    renderBuildNeeds(history.brain);
  };

  /**
   * The header read, through the node's throttle windows. The panel prices
   * itself from this answer and shows dashes without it, so giving up after one
   * look would leave a paying visitor with a dead panel until they reloaded.
   */
  const readHeaderWithRetries = async (rounds: number): Promise<void> => {
    for (let round = 0; round < rounds; round += 1) {
      try {
        await readHeader();

        return;
      } catch {
        await new Promise((resolve) => window.setTimeout(resolve, 2_500));
      }
    }
  };

  void readHeaderWithRetries(6);

  // ---- the Build view: the tapes the chain carries ---------------------------
  mountBuild();

  // ---- the arena: the run bundled with the page, stepped here -----------------
  // The plate runs only while it is on screen, so a hidden tab never steps the
  // arena; its language is the site's, set at boot and on every switch.
  const gameRoot = document.getElementById("view-game");

  if (gameRoot instanceof HTMLElement) {
    const game = new GameView(gameRoot, `${import.meta.env.BASE_URL}game-session.json`);

    game.setActive(currentRoute() === "game");
    relabelGame = () => game.relabel();
    gameView = game;
  }

  // ---- the brain: the fly scope, live three.js -------------------------------
  const meta = await fetchMeta();
  const [geometry, connectome] = await Promise.all([fetchGeometry(meta), fetchConnectome()]);

  const mount = document.querySelector("[data-od-id='brain-3d-placeholder']");
  const stageEl = document.querySelector("[data-od-id='brain-stage']");

  if (!(mount instanceof HTMLElement) || !(stageEl instanceof HTMLElement))
    throw new Error("missing the brain stage");

  const groups = cellGroups(geometry);
  const descendingClass = geometry.meta.superClasses.indexOf("descending");
  const descending = new Uint8Array(geometry.superClasses.length);

  for (let i = 0; i < descending.length; i += 1) {
    descending[i] = geometry.superClasses[i] === descendingClass ? 1 : 0;
  }

  const board = new Board({ geometry, connectome, groups, address: history.address });

  const telemetry = new Telemetry(geometry, connectome);

  relabelBoard = () => {
    board.relabel();
    telemetry.relabel();
  };

  telemetry.setThreshold(history.brain?.threshold ?? 0);
  telemetry.setHistory(history.ticks, history.feeds);
  board.setHistory(history.ticks);
  board.setBlock(history.block?.number ?? history.brain?.headBlock ?? 0);
  board.setBrain(history.brain?.tapes ?? null, history.brain?.threshold ?? 0);
  void fetchTapes()
    .then((manifest) => board.setTapeSizes(manifest.tapes.map((tape) => tape.bytes)))
    .catch(() => {});

  // ---- the effect layer's own seam --------------------------------------------
  // What the reaction layer does is invisible to a query: a strike is a shader uniform,
  // not markup. These attributes are how the browser smoke sees it - written on
  // transitions only, never per frame, so the stage pays nothing for being testable.
  const fxSeam = (name: string, value: string): void => {
    stageEl.setAttribute(`data-fx-${name}`, value);
  };

  const view = new FlyScope({
    mount,
    observe: document.getElementById("bdObsMount"),
    geometry,
    connectome,
    reduced: REDUCED,
    descending,
    onFrame: (frame, project) => {
      board.frame(frame, project, view.turn);

      if (board.idCell >= 0) board.setIdPosition(view.cellPosition(board.idCell));
    },
    onQuality: (quality) => fxSeam("quality", String(quality)),
  });

  stageView = view;
  stageView.setPaused(currentRoute() !== "brain");

  // dev-only handle: lets a local check drive a feed or a thought without a chain write
  if (import.meta.env.DEV) Object.assign(window, { flyScope: view });

  for (const tick of document.querySelectorAll<HTMLElement>(".bd-axis [data-go]")) {
    tick.addEventListener("click", () => view.goTo(Number(tick.dataset.go)));
  }

  let bursts = 0;
  let firingTimer = 0;

  const markBurst = (neurons: number): void => {
    bursts += 1;
    fxSeam("bursts", String(bursts));
    fxSeam("neurons", String(neurons));
    fxSeam("firing", "1");
    window.clearTimeout(firingTimer);
    firingTimer = window.setTimeout(() => fxSeam("firing", "0"), BURST_SECONDS * 1_000);
  };

  fxSeam("bursts", "0");
  fxSeam("firing", "0");
  fxSeam("quality", String(view.quality));

  // ---- the potential field ---------------------------------------------------
  const brain: BrainInfo | null = history.brain;
  let potentials: Int16Array | null = null;
  let previous: Int16Array | null = null;
  let fieldTick = brain?.tick ?? 0;

  // a pre-seal kernel reads threshold 0 on-chain; 0/0 would paint NaN
  const threshold = (): number => brain?.threshold || 12_800;

  // ---- the state rail: the chain's report of the brain, said in its terms ----
  // It is mounted before the field is read so the first paint can fill both the
  // wiring from the header and the newest thought the window already holds.
  vitals = mountVitals({ explorer: () => explorerBase });
  vitals.setHistory(history.ticks);
  vitals.updateBrain(brain);
  vitals.setField(null, null, threshold());
  {
    const newest = newestThought(history.ticks);
    // the chain's own record of the last poke, so the sense zone opens on the
    // burn the feed log below is already showing
    const lastFeed = history.feeds[0];

    if (lastFeed !== undefined) vitals.poke(lastFeed, false);

    if (newest === null) vitals.idle();
    else vitals.thought(newest, false);
  }

  const paint = (): void => {
    view.setField(potentials, threshold());
    board.setField(potentials, threshold());
  };

  const refreshPotentials = async (): Promise<void> => {
    try {
      const next = await fetchPotentials(fieldTick);

      // a short read is a failed read: the indexer answers 200 with an empty body when its
      // own field read fails, and half a field is not a picture worth painting
      if (next.length < geometry.meta.neurons) return;

      previous = potentials;
      potentials = next;

      paint();
      vitals?.setField(potentials, previous, threshold());
    } catch {
      // the field is a heavy read on a rate-limited node; the next event retries
    }
  };

  paint();
  window.setInterval(() => {
    void refreshPotentials();
  }, 30_000);

  // ---- tonight's fired neurons, newest first --------------------------------
  const specimens: Specimen[] = [];
  const seen = new Set<number>();
  const amountByTx = new Map<string, string>();
  const feederByTx = new Map<string, string>();

  for (const feed of history.feeds) {
    amountByTx.set(feed.txHash, fmt(Math.round(Number(BigInt(feed.amount) / 10n ** 18n))));
    feederByTx.set(feed.txHash, feed.feeder);
  }

  let firedWindow = firingCount(history.ticks);

  /**
   * Turns firings into specimens at the top of the table.
   *
   * The genomes are derived here (real annotation, real keccak), so a firing is
   * a specimen only once this returns: the table is re-rendered with the new
   * rows, and the window keeps its own size - the oldest row leaves first, and
   * never the row the visitor has already picked.
   */
  async function addFirings(firings: readonly Firing[]): Promise<number> {
    let added = 0;

    for (const firing of firings) {
      const feeder = feederByTx.get(firing.tick.txHash) ?? "";

      for (const neuron of firing.neurons) {
        if (seen.has(neuron)) continue;

        seen.add(neuron);
        specimens.push({
          neuron,
          tick: firing.tick.tick,
          txHash: firing.tick.txHash,
          stateRoot: firing.tick.stateRoot,
          raw: firing.tick,
          genome: await deriveGenome(thoughtFromTick(firing.tick, feeder), neuron),
        });
        added += 1;
      }
    }

    while (specimens.length > SPECIMENS) {
      // from the tail: the oldest row leaves, and never the picked one
      let at = specimens.length - 1;

      while (at > 0 && specimens[at].neuron === pickedNeuron) at -= 1;

      if (specimens[at].neuron === pickedNeuron) break;

      specimens.splice(at, 1);
    }

    return added;
  }

  await addFirings(newestFirings(history.ticks));

  void renderFiredGrid(specimens);
  wireWizard(specimens, feederByTx);

  setText("firedCount", t("hatch.firedCount", { n: String(specimens.length) }));
  setText("tonightCount", fmt(firedWindow));

  // ---- the stage's live tick + the tape --------------------------------------
  // The newest recorded thought is still warm when a viewer arrives: the stage opens on
  // the last firing the chain actually recorded instead of on a dead brain. It is a
  // replay of the indexer's own tick history - the stage names the tick it is animating,
  // and the tape below carries that tick with its real fired count - and the first live
  // thought replaces it outright. Only a thought names a tick: the activity rows the
  // indexer writes per block carry firings of their own and a block height in the same
  // field, so a replay that took the newest row with firings would name a block as the
  // brain's age.
  const newestFiring = newestThought(history.ticks);

  let railTick = newestFiring?.tick ?? brain?.tick ?? 0;

  setText("stageTick", t("stage.live", { n: fmt(railTick) }));

  if (newestFiring !== null) {
    view.thought(newestFiring.fired);
    markBurst(newestFiring.fired.length);
  }

  const recent = history.ticks.filter(isThought).slice(-3);

  // a quiet chain must not leave the replayed thought off the record
  if (newestFiring !== null && !recent.includes(newestFiring)) recent.unshift(newestFiring);

  for (const tick of recent) {
    pushTicker(tapeLabel(tick, tick.tick, amountByTx.get(tick.txHash) ?? null));
  }

  // the registry is the real claim index: the chain-wide claim list, each row
  // joined to the thought it names. The registry answer is the pageless half
  // and the thought receipts are the slow half, so the whole read is retried
  // while the throttling node refuses - never replaced by tonight's window.
  let collectionClaims: readonly Claimed[] | null = null;

  /** One claim read, painted in place: the first load and every later refresh. */
  const loadClaims = (): void => {
    void retried(
      () => readClaimed(history.address),
      (claimed) => {
        collectionClaims = claimed;
        renderHatchedList(claimed);
        void renderCollectionGrid(claimed);
      },
    );
  };

  loadClaims();
  refreshClaims = loadClaims;

  // the explorer the launch card links to is the chain's own answer, not ours
  if (history.explorer !== "") explorerBase = history.explorer.replace(/\/$/, "");

  rerenderSurfaces = () => {
    void renderFiredGrid(specimens);
    setText("firedCount", t("hatch.firedCount", { n: String(specimens.length) }));
    setText("stageTick", t("stage.live", { n: fmt(railTick) }));
    renderBuildTapes();
    renderBuildIndex();
    renderBuildDecoded();
    mountDocs(docsSlug());
    feedPanel?.relanguage();
    vitals?.relanguage();

    const pickedSpecimen =
      pickedNeuron === null
        ? undefined
        : specimens.find((candidate) => candidate.neuron === pickedNeuron);

    if (pickedSpecimen !== undefined) {
      picked = rowOf(pickedSpecimen);
      renderPickedPlate(pickedSpecimen, feederByTx);
    }

    if (lastCard !== null) renderHatchPlate(lastCard.genome);
    else if (launchState === "idle" && pickedSpecimen !== undefined)
      renderHatchPlate(pickedSpecimen.genome);

    if (collectionClaims !== null) {
      renderHatchedList(collectionClaims);
      void renderCollectionGrid(collectionClaims);
    }
  };

  // ---- the feed: the visitor burns OBRAIN and the brain thinks ---------------
  // The brain address is the indexer's own chain identity (chain.address), and
  // the prices, token and dead address arrive from the brain contract through
  // readHeader: nothing about this flow is remembered in the page.
  feedPanel = mountFeedPanel({
    brain: history.address,
    explorer: explorerBase,
    chain: feedState,
    wallet: () => walletAddress,
    onBurned: (burn) => {
      // the burn opened a thought: the field has moved, so read it again, and
      // the rail shows the poke the visitor just paid for before the chain
      // answers - the receipt of the press, not of the read
      mineTx = burn.txHash;
      vitals?.poke(burn, true);
      view.poke(burn.channel);
      board.poke(burn.channel, view.cellPosition(burn.channel % 60));

      window.setTimeout(() => {
        void refreshPotentials();
      }, 2_500);

      void readHeaderWithRetries(2);
    },
  });

  // the chain's own record of burns opens the log; the stream keeps it live
  feedPanel?.setLog(history.feeds);

  // ---- the live stream ---------------------------------------------------------
  openStream({
    onHistory: () => {},
    onTick: (tick) => {
      // the indexer's per-block activity rows arrive exactly like thoughts, for
      // the stage's liveliness; their `tick` is a block height, so they never
      // move a readout that claims to be the brain's own state
      if (isThought(tick)) {
        railTick = tick.tick;
        vitals?.thought(tick, mineTx !== "" && tick.txHash === mineTx);
        setText("stageTick", t("stage.live", { n: fmt(railTick) }));

        // the night's fired count is the specimens' own count: the same rows the
        // hatch table offers, so the number and the table cannot disagree
        firedWindow += firingCount([tick]);
        setText("tonightCount", fmt(firedWindow));
      }

      if (isThought(tick)) {
        pushTicker(tapeLabel(tick, tick.tick, amountByTx.get(tick.txHash) ?? null));
      }

      // only a thought is something the fly's brain did: the per-block activity
      // rows carry no transaction and stay off the specimen
      if (isThought(tick) && tick.fired.length > 0) {
        view.thought(tick.fired);
        board.thought(tick);
        telemetry.thought(tick);
        markBurst(tick.fired.length);
      }

      // a thought streamed in is a specimen the hatch table has to offer: it
      // arrives before the visitor picks, and the genome is derived off the
      // render path so the stage never waits on it
      void addFirings(newestFirings([tick])).then((added) => {
        if (added === 0) return;

        void renderFiredGrid(specimens);
        setText("firedCount", t("hatch.firedCount", { n: String(specimens.length) }));
      });

      fieldTick = Math.max(fieldTick, tick.tick);

      window.setTimeout(() => {
        void refreshPotentials();
      }, 1_800);
    },
    onFeed: (feed) => {
      feedPanel?.pushFeed(feed);
      telemetry.feed(feed);
      vitals?.poke(feed, feed.txHash === mineTx);

      if (feed.txHash !== mineTx) {
        view.poke(feed.channel);
        board.poke(feed.channel, view.cellPosition(feed.channel % 60));
      }

      amountByTx.set(feed.txHash, fmt(Math.round(Number(BigInt(feed.amount) / 10n ** 18n))));

      window.setTimeout(() => {
        void refreshPotentials();
      }, 2_000);
    },
    onBlock: (block) => {
      board.setBlock(block.number);
      telemetry.block(block);
      gameView?.onBlock(block);
    },
    onStatus: () => {},
  });
}

void start().catch((error: Error) => {
  pushTicker(() => error.message);
});
